import { Col, Form, Modal, Row } from "react-bootstrap";
import FloatingInput from "../compoment/ui/FloatingInput";
import PhoneInputWithCountry from "../compoment/ui/PhoneInputWithCountry";
import PasswordRequirementsHints from "../compoment/ui/PasswordRequirementsHints";
import Loader from "../compoment/ui/Loader";
import EyeOpen from "../compoment/icon/EyeOpen";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useForm } from "../constants/useForm";
import { toast } from "react-toastify";
import LogoBlack from "./../assets/img/MOSIDA-black.png";
import {
  authService,
  preSignupCheckPhoneApi,
  sendPhoneVerificationApi,
  verifyPhoneVerificationApi,
} from "./auth/authService";
import { AuthUser, SignupFormValues } from "./auth/types";
import { validateSignup } from "./auth/validation";
import { persistAuthUser } from "../utils/authSession";
import {
  DEFAULT_COUNTRY_ISO2,
  getCountryByIso2,
} from "./auth/countryCodes";
import {
  allowOnlyOtpDigits,
  buildE164Phone,
  isPhoneValidForOtp,
} from "./auth/phoneUtils";
import TermsOfUseModal from "./TermsOfUseModal";
import PrivacyPolicyModal from "./PrivacyPolicyModal";

interface SignupModalProps {
  show: boolean;
  onHide: () => void;
  props?: Record<string, unknown>;
  setSignupModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setSigninModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailVerificationModal?: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenSignin?: () => void;
  onOpenEmailVerification?: () => void;
  onGoogleAuth?: () => Promise<void> | void;
  onAuthSuccess?: (user: AuthUser) => Promise<void> | void;
}

type PendingSignupData = {
  formValues: SignupFormValues;
  phone_number: string;
};

const SignupModal: React.FC<SignupModalProps> = ({
  show,
  onHide,
  setSignupModal,
  setSigninModal,
  setEmailVerificationModal,
  onOpenSignin,
  onOpenEmailVerification,
  onAuthSuccess,
  props,
}) => {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleRePassword, setVisibleREPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryIso2, setCountryIso2] = useState(DEFAULT_COUNTRY_ISO2);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [pendingPhone, setPendingPhone] = useState("");
  const [flowError, setFlowError] = useState<string | null>(null);
  const [showBackAfterError, setShowBackAfterError] = useState(false);
  const pendingSignupRef = useRef<PendingSignupData | null>(null);

  const { values, handleChange, setValues, resetForm } = useForm({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });

  const typedValues = values as SignupFormValues;

  const dialCode =
    getCountryByIso2(countryIso2)?.dialCode ||
    getCountryByIso2(DEFAULT_COUNTRY_ISO2)!.dialCode;

  const resetOtpFlow = () => {
    setShowOtpModal(false);
    setOtpCode("");
    setPendingPhone("");
    setFlowError(null);
    setShowBackAfterError(false);
    pendingSignupRef.current = null;
  };

  const resetAllLocalState = () => {
    resetOtpFlow();
    resetForm();
    setPhoneNumber("");
    setCountryIso2(DEFAULT_COUNTRY_ISO2);
    setShowTermsModal(false);
    setShowPrivacyModal(false);
    setVisiblePassword(false);
    setVisibleREPassword(false);
  };

  useEffect(() => {
    if (!show) {
      resetAllLocalState();
    }
  }, [show]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationError = validateSignup(typedValues);
    if (validationError) return toast.error(validationError);

    const phone_number = buildE164Phone(phoneNumber, dialCode);
    if (!isPhoneValidForOtp(phone_number)) {
      return toast.error(
        "Enter a valid phone number with at least 10 digits."
      );
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const check = await preSignupCheckPhoneApi({ phone_number });
      const allowed =
        check?.allowed !== false &&
        check?.data?.allowed !== false &&
        check?.data?.is_available !== false;
      if (allowed === false) {
        throw new Error(
          check?.message ||
            check?.data?.message ||
            "This phone number cannot be used for signup."
        );
      }

      await sendPhoneVerificationApi({
        phone_number,
        purpose: "signup",
      });

      pendingSignupRef.current = {
        formValues: { ...typedValues },
        phone_number,
      };
      setPendingPhone(phone_number);
      setOtpCode("");
      setShowOtpModal(true);
      toast.success("Verification code sent.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Could not send code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (!pendingPhone || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendPhoneVerificationApi({
        phone_number: pendingPhone,
        purpose: "signup",
      });
      setOtpCode("");
      toast.success("A new code was sent.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Could not resend code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTPAndRegister = async (
    e: FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    const code = allowOnlyOtpDigits(otpCode, 6);
    if (code.length < 6) {
      return toast.error("Enter the 6-digit code.");
    }
    if (!pendingPhone) {
      toast.error("Send a verification code to your phone first.");
      setShowOtpModal(false);
      return;
    }
    if (!pendingSignupRef.current) {
      toast.error("Something went wrong. Please fill the form and try again.");
      resetOtpFlow();
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setFlowError(null);
    setShowBackAfterError(false);
    try {
      await verifyPhoneVerificationApi({
        phone_number: pendingPhone,
        code,
      });

      const { formValues, phone_number } = pendingSignupRef.current;
      try {
        const { user } = await authService.signup(formValues, {
          phone_number,
        });
        persistAuthUser(user);
        await onAuthSuccess?.(user);
        resetOtpFlow();
        onHide();
        if (!onAuthSuccess) {
          onOpenEmailVerification?.();
          setEmailVerificationModal?.(true);
        }
        toast.success(
          "Congratulations! 🎉 Your account has been created and verified."
        );
      } catch (registerErr: unknown) {
        const message =
          registerErr instanceof Error
            ? registerErr.message
            : "Signup failed. Please try again.";
        setFlowError(message);
        setShowBackAfterError(true);
        toast.error(message);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Verification failed. Please try again.";
      setFlowError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToSignup = () => {
    const pending = pendingSignupRef.current;
    if (pending) {
      setValues({ ...pending.formValues });
    }
    setShowOtpModal(false);
    setOtpCode("");
    setFlowError(null);
    setShowBackAfterError(false);
  };

  return (
    <>
      {isSubmitting && <Loader />}
      <Modal
        className="mosidaModal signupModal"
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={show && !showOtpModal}
        onHide={onHide}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <Col md={10} className="mx-auto">
            <div className="text-center mb-4" style={{ marginTop: "10px" }}>
              <img
                className="pointer"
                style={{ width: 140 }}
                src={LogoBlack}
                alt="MOSIDA logo"
              />
            </div>
            <div className="sectionHeading text-md-center mb-4 pb-2">
              Sign Up to MOSIDA
            </div>
            <Form onSubmit={handleSubmit}>
              <Row className="g-2">
                <Col md={6} xs={6}>
                  <FloatingInput
                    rootClassName="floatingInput--credential"
                    placeholder="Enter first name"
                    label="First Name"
                    icon={undefined}
                    type="text"
                    name="firstName"
                    value={typedValues.firstName}
                    autoComplete="given-name"
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6} xs={6}>
                  <FloatingInput
                    rootClassName="floatingInput--credential"
                    placeholder="Enter last name"
                    label="Last Name"
                    icon={undefined}
                    type="text"
                    name="lastName"
                    value={typedValues.lastName}
                    autoComplete="family-name"
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="Enter username"
                label="Username"
                icon={undefined}
                type="text"
                name="username"
                value={typedValues.username}
                autoComplete="username"
                onChange={handleChange}
              />

              <PhoneInputWithCountry
                rootClassName="floatingInput--signupPhone"
                placeholder="Phone number"
                label="Phone number"
                name="phoneNumber"
                phoneNumber={phoneNumber}
                onPhoneNumberChange={setPhoneNumber}
                countryIso2={countryIso2}
                onCountryChange={setCountryIso2}
              />

              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="example@gmail.com"
                label="E-mail"
                icon={undefined}
                type="text"
                name="email"
                value={typedValues.email}
                autoComplete="email"
                onChange={handleChange}
              />

              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="••••••••"
                label="Password"
                icon={EyeOpen}
                type="password"
                name="password"
                value={typedValues.password}
                autoComplete="new-password"
                onChange={handleChange}
                setVisiblePassword={setVisiblePassword}
                visiblePassword={visiblePassword}
              />
              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="••••••••"
                label="Repeat Password"
                icon={EyeOpen}
                type="password"
                name="confirmPassword"
                value={typedValues.confirmPassword}
                autoComplete="new-password"
                onChange={handleChange}
                setVisiblePassword={setVisibleREPassword}
                visiblePassword={visibleRePassword}
              />
              <PasswordRequirementsHints
                password={typedValues.password}
                confirmPassword={typedValues.confirmPassword}
              />
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="flexCheckChecked"
                  checked={typedValues.acceptTerms}
                  name="acceptTerms"
                  onChange={handleChange}
                  aria-label="Accept terms and privacy policy"
                />
                <label
                  className="form-check-label"
                  htmlFor="flexCheckChecked"
                  style={{ color: "var(--input-auth)" }}
                >
                  I agree to the{" "}
                  <button
                    type="button"
                    className="privacyLink"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setShowTermsModal(true);
                    }}
                  >
                    Terms of Use
                  </button>{" "}
                  and{" "}
                  <button
                    type="button"
                    className="privacyLink"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setShowPrivacyModal(true);
                    }}
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>
              <button
                className="btnPrimary w-100 sqaure mt-2"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending code..." : "Sign Up"}
              </button>
            </Form>
            <div className="alreadyHaveAccount">
              {`Already have an account?`}
              <span
                className="text-primary"
                onClick={() => {
                  onOpenSignin?.();
                  setSigninModal?.(true);
                  setSignupModal?.(false);
                }}
              >
                Sign in
              </span>
            </div>
          </Col>
        </Modal.Body>
      </Modal>

      <Modal
        className="mosidaModal signupModal"
        size="lg"
        aria-labelledby="signup-otp-modal-title"
        centered
        show={show && showOtpModal}
        onHide={handleBackToSignup}
        backdrop="static"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <Col md={10} className="mx-auto">
            <div className="text-center mb-4" style={{ marginTop: "30px" }}>
              <img
                className="pointer"
                style={{ width: 140 }}
                src={LogoBlack}
                alt="MOSIDA logo"
              />
            </div>
            <div
              id="signup-otp-modal-title"
              className="sectionHeading text-md-center mb-2 pb-1"
            >
              Verify your phone
            </div>
            <p className="text-muted text-md-center mb-4">
              Enter the code we sent to <strong>{pendingPhone}</strong>
            </p>
            <Form onSubmit={handleVerifyOTPAndRegister}>
              <FloatingInput
                rootClassName="floatingInput--credential"
                placeholder="Enter verification code"
                label="Verify OTP"
                type="tel"
                name="verificationCode"
                value={otpCode}
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                onChange={(e) =>
                  setOtpCode(allowOnlyOtpDigits(e.target.value, 6))
                }
              />
              {flowError && (
                <p
                  className="text-danger text-center mb-3"
                  role="alert"
                  style={{ fontSize: "13px" }}
                >
                  {flowError}
                </p>
              )}
              <button
                className="btnPrimary w-100 sqaure"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Verifying..." : "Verify & Sign Up"}
              </button>
            </Form>
            <div className="text-center mt-3 d-flex flex-column align-items-center gap-2">
              <button
                type="button"
                className="btn btn-link p-0"
                style={{
                  fontSize: "14px",
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                }}
                onClick={handleResendOTP}
                disabled={!pendingPhone || isSubmitting}
              >
                Resend code
              </button>
              {showBackAfterError && (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  style={{
                    fontSize: "14px",
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                  }}
                  onClick={handleBackToSignup}
                  disabled={isSubmitting}
                >
                  Back
                </button>
              )}
            </div>
          </Col>
        </Modal.Body>
      </Modal>

      <TermsOfUseModal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
      />
      <PrivacyPolicyModal
        show={showPrivacyModal}
        onHide={() => setShowPrivacyModal(false)}
      />
    </>
  );
};

export default SignupModal;
